import { ClientEndpoint, ClientEndpointType, ClientEndpoints, EventTriggerClientEndpoint } from "@app/shared";
import { useEffect, useState } from "react";
import { useFetch } from "@app/shared/src/hooks";
import { EditEndpointConfig } from "../EditEndpointConfig/EditEndpointConfig";
import { DropDownMenu, IconLink, Icons } from "@app/dashboard/src/components/shared";
import { Select } from "@app/dashboard/src/components/shared/inputs";

type EventTriggerClientEndpointsEditorProps = {
    organizationId?: string;
    facilityId?: string;
    eventTriggerClientEndpoints?: EventTriggerClientEndpoint[];
    onChange: (endpoints: EventTriggerClientEndpoint[]) => void;
};

export const EventTriggerClientEndpointsEditor: React.FC<EventTriggerClientEndpointsEditorProps> = (props) => {
    const [endpoints] = useFetch(() => ClientEndpoints.getAll(props.organizationId, props.facilityId));
    const [availableEndpoints, setAvailableEndpoints] = useState<ClientEndpoint[]>([]);

    useEffect(() => {
        if (endpoints) {
            const usedEndpointIds = new Set(props.eventTriggerClientEndpoints?.map((etc) => etc.clientEndpointId) || []);
            // Filter out Luxcom endpoints - they are handled by LuxcomBiuSettings
            setAvailableEndpoints(endpoints.filter((e) => !usedEndpointIds.has(e.id) && e.endpointType !== ClientEndpointType.Luxcom));
        }
    }, [endpoints, props.eventTriggerClientEndpoints]);

    const handleAddEndpoint = (endpointId: string) => {
        const endpoint = endpoints?.find((e) => e.id === endpointId);
        if (!endpoint) return;

        const newEndpointRelation: EventTriggerClientEndpoint = new EventTriggerClientEndpoint({
            eventTriggerId: undefined,
            clientEndpointId: endpointId,
            clientEndpoint: undefined,
            endpointConfig: "",
            minValue: undefined,
            maxValue: undefined,
            additionalValue: undefined,
        });
        // Assigned after construction rather than passed in: the constructor takes the generated wire
        // interface (IClientEndpoint, dates as strings) while `endpoint` is the hydrated class (dates as
        // Date), so passing it through meant hand-converting every date field — which silently broke the
        // build each time the endpoint model gained another one. The field itself is typed as the class,
        // so the instance goes straight in. Spread to keep it a copy of the fetched list's item.
        newEndpointRelation.clientEndpoint = { ...endpoint };

        const updatedEndpoints = [...(props.eventTriggerClientEndpoints || []), newEndpointRelation];
        props.onChange(updatedEndpoints);
    };

    const handleRemoveEndpoint = (endpointId: string) => {
        const updatedEndpoints = (props.eventTriggerClientEndpoints || []).filter((etc) => etc.clientEndpointId !== endpointId);
        props.onChange(updatedEndpoints);
    };

    const handleEndpointConfigChange = (endpointId: string, config: string) => {
        const updatedEndpoints = (props.eventTriggerClientEndpoints || []).map((etc) => (etc.clientEndpointId === endpointId ? { ...etc, endpointConfig: config } : etc));
        props.onChange(updatedEndpoints);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-medium">Endpoint Notifications</h4>
                {availableEndpoints.length > 0 && (
                    <Select
                        label=""
                        emptyOptionText="Add Endpoint..."
                        emptyOptionDisabled={false}
                        options={availableEndpoints.map((e) => ({ label: e.endpointName, value: e.id }))}
                        value=""
                        onChange={(e) => {
                            if (e.target.value) {
                                handleAddEndpoint(e.target.value);
                                e.target.value = ""; // Reset selection
                            }
                        }}
                    />
                )}
            </div>

            {(!props.eventTriggerClientEndpoints || props.eventTriggerClientEndpoints.length === 0) && <div className="text-gray-500 italic p-4 border-2 border-dashed border-gray-300 rounded">No endpoints configured. Add an endpoint to receive notifications for this trigger.</div>}

            {props.eventTriggerClientEndpoints?.map((etc, index) => (
                <div key={etc.clientEndpointId || index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                        <h5 className="font-medium text-gray-900">{etc.clientEndpoint?.endpointName || "Unknown Endpoint"}</h5>
                        <DropDownMenu>
                            <IconLink
                                onClick={() => {
                                    if (etc.clientEndpointId) {
                                        handleRemoveEndpoint(etc.clientEndpointId);
                                    }
                                }}
                                Icon={Icons.Trash}
                            >
                                Remove
                            </IconLink>
                        </DropDownMenu>
                    </div>

                    <EditEndpointConfig
                        endpoint={etc.clientEndpoint}
                        value={etc.endpointConfig}
                        onChange={(config) => {
                            const endpointId = etc.clientEndpointId;
                            if (endpointId) {
                                handleEndpointConfigChange(endpointId, config || "");
                            }
                        }}
                    />
                </div>
            ))}
        </div>
    );
};
